/** Browser mirror of api/ai/lease-returns/trueup.mjs — keep in sync. */
/**
 * Deterministic redelivery true-up / settlement calculator.
 * The LLM extracts parameters; this module owns all arithmetic and the formula trail.
 * Pure JS — safe to mirror into web/src/lease-return-trueup.js for client recompute.
 */

/** @typedef {'delta_vs_delivery'|'full_life'|'half_life'|'reserves_only'|'hybrid'|'serviceable_only'} EolModality */

/**
 * Compound escalation: rate × (1 + esc)^years.
 * Default years = returnYear − baseYear. Override with compoundYears when the
 * amendment's worked example uses a different anniversary count (GOL 39613 → 18).
 */
export function escalateRate(baseRate, escalationPct, baseYear, returnYear, { compoundYears = null } = {}) {
  const base = Number(baseRate);
  const esc = Number(escalationPct);
  const years =
    compoundYears != null
      ? Number(compoundYears)
      : Number(returnYear) - Number(baseYear);
  if (!Number.isFinite(base) || !Number.isFinite(esc) || !Number.isFinite(years) || years < 0) {
    return { rate: null, years: null, formula: null, error: "missing escalation inputs" };
  }
  const rate = base * Math.pow(1 + esc, years);
  return {
    rate,
    years,
    formula: `${fmtMoney(base)} × (1 + ${pctLabel(esc)})^${years} = ${fmtMoney(rate)}`,
  };
}

export function fmtMoney(n, { currency = "USD" } = {}) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const abs = Math.abs(Number(n));
  const formatted = abs.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const sign = Number(n) < 0 ? "−" : "";
  return `${currency} ${sign}${formatted}`;
}

function pctLabel(esc) {
  const p = Number(esc) * 100;
  return Number.isInteger(p) ? `${p}%` : `${p.toFixed(2)}%`;
}

function num(v) {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function sourceOk(src) {
  if (!src) return false;
  if (src === "MANUAL" || src?.kind === "MANUAL") return true;
  if (src.doc || src.documentId || src.sheet || src.section || src.page) return true;
  return false;
}

/**
 * Engine PRSV module line (GOL-style delta / pay-per-FH-since-PRSV).
 * adjustment = escalatedRate × moduleSplit × FH_since_PRSV
 */
export function computePrsvModuleLine({
  label,
  serial,
  baseRate,
  escalationPct = 0.03,
  baseYear,
  returnYear,
  compoundYears = null,
  escalatedRate = null, // when amendment states the year rate explicitly
  moduleSplit,
  fhSincePrsv,
  oneWay = true,
  citation = null,
  techSource = null,
}) {
  const split = num(moduleSplit);
  const fh = num(fhSincePrsv);
  // Prefer live escalation from baseRate × as-of year so changing asOfDate re-prorates.
  // Fall back to a stated escalatedRate only when baseRate/baseYear are absent.
  let rate = null;
  let rateFormula = null;
  if (num(baseRate) != null && (baseYear != null || compoundYears != null)) {
    const esc = escalateRate(baseRate, escalationPct, baseYear, returnYear, { compoundYears });
    rate = esc.rate;
    rateFormula = esc.formula;
    if (esc.error) return missingLine(label, esc.error, citation);
  } else if (num(escalatedRate) != null) {
    rate = num(escalatedRate);
    rateFormula = `${fmtMoney(rate)} (stated escalated rate — set base rate + base year to re-escalate with as-of date)`;
  } else {
    return missingLine(label, "missing base rate / base year (or stated escalated rate)", citation);
  }
  if (split == null || fh == null || rate == null) {
    return missingLine(label, "missing rate, module split, or FH since PRSV", citation);
  }
  if (!sourceOk(citation) && !sourceOk(techSource)) {
    return missingLine(label, "missing provenance", citation);
  }
  const amount = rate * split * fh;
  const formula = [
    rateFormula,
    `× ${split} module split × ${fh.toLocaleString("en-US")} FH`,
    `= ${fmtMoney(amount)}`,
  ].join(" ");
  return {
    item: label,
    serial: serial || null,
    basis: formula,
    amount: round2(amount),
    direction: amount >= 0 ? "lessee_to_lessor" : "lessor_to_lessee",
    oneWay: Boolean(oneWay),
    source: citation,
    techSource: techSource || null,
    flags: techSource?.kind === "projected" ? ["input = projected"] : [],
    ok: true,
  };
}

/** LLP: listPrice × (cyclesUsedDelta / lifeLimit) for delta; shortfall form for full_life. */
export function computeLlpStackLine({
  label,
  serial,
  modality = "delta_vs_delivery",
  llps = [],
  citation = null,
}) {
  if (!Array.isArray(llps) || !llps.length) {
    return {
      item: label,
      serial: serial || null,
      basis: "LLP stack missing",
      amount: null,
      direction: null,
      source: citation,
      flags: ["RED", "LLP stack missing — request status"],
      ok: false,
      missing: true,
    };
  }
  let sum = 0;
  const parts = [];
  for (const part of llps) {
    const price = num(part.listPrice);
    const life = num(part.lifeLimit);
    const used = num(part.cyclesUsedDelta ?? part.cyclesAtReturn);
    const remaining = num(part.cyclesRemainingShortfall);
    if (price == null || life == null || life === 0) {
      return missingLine(label, `LLP ${part.name || "?"} missing price/life`, citation);
    }
    let share;
    if (modality === "full_life") {
      if (remaining == null) return missingLine(label, `LLP ${part.name} missing shortfall`, citation);
      share = remaining / life;
    } else {
      if (used == null) return missingLine(label, `LLP ${part.name} missing cycles`, citation);
      share = used / life;
    }
    const lineAmt = price * share;
    sum += lineAmt;
    parts.push(`${part.name || "LLP"}: ${fmtMoney(price)} × ${(share * 100).toFixed(2)}%`);
  }
  return {
    item: label,
    serial: serial || null,
    basis: `Σ ${parts.join("; ")} = ${fmtMoney(sum)}`,
    amount: round2(sum),
    direction: sum >= 0 ? "lessee_to_lessor" : "lessor_to_lessee",
    source: citation,
    flags: [],
    ok: true,
  };
}

/**
 * EOL Credit: min(creditCap, engineAdjustment); unused cancelled (never cash).
 * Void when companionDefault = true.
 */
export function applyEolCredit({
  label = "EOL Credit",
  creditCap,
  engineAdjustment,
  companionDefault = false,
  citation = null,
}) {
  const cap = num(creditCap);
  const adj = num(engineAdjustment);
  if (cap == null || adj == null) return missingLine(label, "missing credit cap or adjustment", citation);
  if (companionDefault) {
    return {
      item: label,
      basis: "Credit void — companion lease / this lease in Default or EoD",
      amount: 0,
      direction: "credit",
      source: citation,
      flags: ["void if any companion lease is in default", "use-it-or-lose-it"],
      ok: true,
      creditUtilized: 0,
      creditCancelled: 0,
      voided: true,
    };
  }
  const utilized = Math.min(cap, Math.max(0, adj));
  const cancelled = Math.max(0, cap - utilized);
  return {
    item: label,
    basis: `min(${fmtMoney(cap)}, ${fmtMoney(adj)}) → utilized ${fmtMoney(utilized)}; ${fmtMoney(cancelled)} cancelled`,
    amount: round2(-utilized), // credit = lessor pays / offset
    direction: "credit",
    source: citation,
    flags: ["use-it-or-lose-it", "unused portion cancelled"],
    ok: true,
    creditUtilized: round2(utilized),
    creditCancelled: round2(cancelled),
    voided: false,
  };
}

/**
 * Rent Premium acceleration: balance at rent date preceding Expiry becomes due on Expiry.
 */
export function rentPremiumAcceleration({
  label = "Rent Premium acceleration",
  serial = null,
  premiumAtPrecedingRentDate,
  expiryDate,
  precedingRentDate,
  citation = null,
}) {
  const bal = num(premiumAtPrecedingRentDate);
  if (bal == null) return missingLine(label, "missing Rent Premium Amount", citation);
  if (!sourceOk(citation)) return missingLine(label, "missing confirmation annex provenance", citation);
  return {
    item: serial ? `${label} — ESN ${serial}` : label,
    serial,
    basis: `Rent Premium Amount at ${precedingRentDate || "preceding rent date"} = ${fmtMoney(bal)}; due on Expiry ${expiryDate || ""}`,
    amount: round2(bal),
    direction: "lessee_to_lessor",
    source: citation,
    flags: ["due on Expiry Date, not at month-end"],
    ok: true,
  };
}

/** LG cliff: if remaining cycles < threshold OR months < threshold → treat remaining as zero. */
export function landingGearCliffArmed({
  cyclesRemaining,
  monthsRemaining,
  thresholdCycles = 3000,
  thresholdMonths = 24,
}) {
  const cyc = num(cyclesRemaining);
  const mo = num(monthsRemaining);
  const byCyc = cyc != null && cyc < thresholdCycles;
  const byMo = mo != null && mo < thresholdMonths;
  return {
    armed: Boolean(byCyc || byMo),
    byCycles: byCyc,
    byMonths: byMo,
    marginCycles: cyc != null ? cyc - thresholdCycles : null,
    marginMonths: mo != null ? mo - thresholdMonths : null,
    thresholdCycles,
    thresholdMonths,
  };
}

export function serviceableOnlyLine({ label, citation = null }) {
  return {
    item: label,
    basis: "condition standard only — defects beyond Manufacturer limits rectified at Lessee expense before redelivery",
    amount: 0,
    direction: "none",
    source: citation,
    flags: ["serviceable_only", "$0 calculated"],
    ok: true,
  };
}

function missingLine(label, reason, citation) {
  return {
    item: label,
    basis: "—",
    amount: null,
    direction: null,
    source: citation || null,
    flags: ["RED", "missing input", reason],
    ok: false,
    missing: true,
  };
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

/**
 * Build settlement table from a trueup-inputs object.
 * Lines are signed: positive = Lessee → Lessor; negative = credit / Lessor → Lessee.
 */
export function computeSettlement(inputs = {}) {
  const lines = [];
  const warnings = [];

  if (inputs.enableEolTrueup === false) {
    return {
      generatedAt: new Date().toISOString(),
      msn: inputs.msn || null,
      asOfDate: inputs.asOfDate || inputs.expiryDate || null,
      expiryDate: inputs.expiryDate || null,
      lines: [],
      netCash: null,
      unresolved: [],
      warnings: [
        {
          code: "eol_disabled",
          text: "EOL true-up calculation is off — turn it on under Settlement parameters to compute.",
        },
      ],
      enabled: false,
    };
  }

  const components = inputs.components || [];
  const paramsByComponent = groupParams(inputs.params || []);
  const layers = inputs.layers || [];
  // User-controlled as-of date drives escalation / proration; projected contract end is reference only
  const asOfDate = inputs.asOfDate || inputs.expiryDate || null;
  const returnYear = yearOf(asOfDate) || num(inputs.returnYear);
  const companionDefault = Boolean(inputs.companionDefault);

  if (asOfDate && inputs.expiryDate && String(asOfDate) !== String(inputs.expiryDate)) {
    warnings.push({
      code: "as_of_override",
      text: `Calculating as of ${asOfDate} (projected contract end ${inputs.expiryDate}). Escalation / proration use the as-of date.`,
    });
  }

  for (const comp of components) {
    const modality = comp.modality || inputs.eolModality || "hybrid";
    const p = paramsByComponent.get(comp.id) || {};

    if (modality === "serviceable_only" || comp.serviceableOnly) {
      lines.push(
        serviceableOnlyLine({
          label: `${compLabel(comp)} — serviceable condition`,
          citation: p.conditionCitation || comp.citation,
        })
      );
      continue;
    }

    if (comp.group === "engine_prsv" || comp.group === "engines") {
      const modules = comp.modules || defaultEngineModules(p);
      for (const mod of modules) {
        const line = computePrsvModuleLine({
          label: `${compLabel(comp)} PRSV — ${mod.name}`,
          serial: comp.serial,
          baseRate: mod.baseRate ?? p.rate,
          escalationPct: mod.escalationPct ?? p.escalation ?? 0.03,
          baseYear: mod.baseYear ?? p.baseYear,
          returnYear: mod.returnYear ?? returnYear,
          compoundYears: mod.compoundYears ?? p.compoundYears ?? null,
          escalatedRate: mod.escalatedRate ?? p.escalatedRate ?? null,
          moduleSplit: mod.split,
          fhSincePrsv: mod.fhSincePrsv ?? comp.atReturn?.sinceSV,
          oneWay: mod.oneWay ?? p.oneWay ?? true,
          citation: mod.citation || p.rateCitation || null,
          techSource: comp.atReturn?.source || null,
        });
        lines.push(line);
        if (comp.atReturn?.source?.kind === "projected") {
          warnings.push({
            code: "projected_inputs",
            text: `${compLabel(comp)} Return-CTA figures are projected (system) — editable estimates.`,
            source: comp.atReturn.source,
          });
        }
      }
    }

    if (comp.group === "engine_llp") {
      lines.push(
        computeLlpStackLine({
          label: `${compLabel(comp)} LLP stack`,
          serial: comp.serial,
          modality: modality === "full_life" ? "full_life" : "delta_vs_delivery",
          llps: comp.llps || [],
          citation: p.llpCitation || comp.citation,
        })
      );
    }

    if (comp.group === "lg") {
      const cliffParam = p.cliff || comp.cliff;
      if (cliffParam) {
        const armed = landingGearCliffArmed({
          cyclesRemaining: comp.atReturn?.cyclesRemaining,
          monthsRemaining: comp.atReturn?.monthsRemaining,
          thresholdCycles: cliffParam.thresholdCycles ?? 3000,
          thresholdMonths: cliffParam.thresholdMonths ?? 24,
        });
        if (armed.armed) {
          warnings.push({
            code: "lg_cliff",
            text: `LG cliff armed (< ${armed.thresholdCycles} cyc / < ${armed.thresholdMonths} mo) — margin cyc ${armed.marginCycles ?? "—"}, mo ${armed.marginMonths ?? "—"}.`,
            source: cliffParam.citation || null,
          });
        }
      }
    }
  }

  // Engine-level EOL credits from layers
  for (const layer of layers) {
    if (layer.type === "eol_credit") {
      const engineAdj = sumEngineAdjustment(lines, layer.serial || layer.componentId);
      const credit = applyEolCredit({
        label: layer.label || `EOL Credit — ${layer.serial || "Engine"}`,
        creditCap: layer.creditCap ?? layer.value,
        engineAdjustment: engineAdj,
        companionDefault: companionDefault || layer.companionDefault,
        citation: layer.citation,
      });
      lines.push(credit);
      if (credit.voided) {
        warnings.push({
          code: "eol_credit_void",
          text: "EOL Credit void — companion-lease / lease Default or EoD.",
          source: layer.citation,
        });
      } else if (credit.ok) {
        warnings.push({
          code: "eol_credit",
          text: `EOL Credit utilized ${fmtMoney(credit.creditUtilized)}; ${fmtMoney(credit.creditCancelled)} cancelled (use-it-or-lose-it).`,
          source: layer.citation,
        });
      }
    }
    if (layer.type === "contribution_rent" || layer.type === "rent_premium_acceleration") {
      const line = rentPremiumAcceleration({
        label: layer.label || "Rent Premium acceleration",
        serial: layer.serial,
        premiumAtPrecedingRentDate: layer.premiumAtPrecedingRentDate ?? layer.value,
        expiryDate: inputs.expiryDate,
        precedingRentDate: layer.precedingRentDate,
        citation: layer.citation,
      });
      lines.push(line);
      if (line.ok) {
        warnings.push({
          code: "rent_premium",
          text: `Rent Premium Amount ${fmtMoney(line.amount)} due on Expiry Date${inputs.expiryDate ? ` (${inputs.expiryDate})` : ""}.`,
          source: layer.citation,
        });
      }
    }
    if (layer.type === "security_deposit" && layer.nonRefundable) {
      warnings.push({
        code: "security_deposit",
        text: "Security deposit retained by Lessor — do not net a refund.",
        source: layer.citation,
      });
    }
    if (layer.type === "settled_payables") {
      warnings.push({
        code: "settled_payables",
        text: layer.summary || "Released vs surviving payables — do not re-invoice discharged amounts.",
        source: layer.citation,
      });
    }
  }

  for (const line of lines) {
    if (line.missing || line.amount == null) {
      warnings.push({
        code: "missing_input",
        text: `${line.item}: ${(line.flags || []).filter((f) => f !== "RED").join("; ") || "missing input"}`,
        source: line.source,
      });
    }
  }

  const net = lines.reduce((acc, l) => (l.amount != null && Number.isFinite(l.amount) ? acc + l.amount : acc), 0);
  const unresolved = lines.filter((l) => l.missing || l.amount == null).map((l) => l.item);

  return {
    generatedAt: new Date().toISOString(),
    msn: inputs.msn || null,
    asOfDate,
    expiryDate: inputs.expiryDate || null,
    returnYear,
    lines: lines.map((l, i) => ({ n: i + 1, ...l })),
    netCash: round2(net),
    unresolved,
    warnings: dedupeWarnings(warnings),
    enabled: true,
  };
}

function compLabel(comp) {
  const ser = comp.serial ? ` (${comp.serial})` : "";
  return `${comp.label || comp.id || "Component"}${ser}`;
}

function defaultEngineModules(p) {
  const fan = num(p.fanSplit) ?? 0.1;
  const core = num(p.coreSplit) ?? 0.9;
  return [
    { name: "Fan Major", split: fan },
    { name: "Core+LPT", split: core },
  ];
}

function groupParams(params) {
  const map = new Map();
  for (const row of params || []) {
    const id = row.componentId || row.id || "_";
    const cur = map.get(id) || {};
    if (row.key) cur[row.key] = row.value;
    if (row.key === "rate") cur.rateCitation = row.citation;
    if (row.key === "cliff") cur.cliff = { ...row.value, citation: row.citation };
    if (row.baseYear != null) cur.baseYear = row.baseYear;
    if (row.unit) cur.unit = row.unit;
    Object.assign(cur, row.extras || {});
    map.set(id, cur);
  }
  return map;
}

function sumEngineAdjustment(lines, serialOrId) {
  if (!serialOrId) {
    return lines
      .filter((l) => l.ok && l.amount != null && /PRSV|LLP/i.test(l.item || "") && l.direction !== "credit")
      .reduce((a, l) => a + (l.amount || 0), 0);
  }
  const key = String(serialOrId);
  return lines
    .filter(
      (l) =>
        l.ok &&
        l.amount != null &&
        l.direction !== "credit" &&
        (String(l.serial || "") === key || String(l.item || "").includes(key))
    )
    .reduce((a, l) => a + Math.max(0, l.amount || 0), 0);
}

function yearOf(dateStr) {
  if (!dateStr) return null;
  const m = String(dateStr).match(/(\d{4})/);
  return m ? Number(m[1]) : null;
}

function dedupeWarnings(list) {
  const seen = new Set();
  const out = [];
  for (const w of list || []) {
    const k = `${w.code}|${w.text}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(w);
  }
  return out;
}

/** Empty inputs shell for persistence. */
export function emptyTrueupInputs({ msn = null } = {}) {
  return {
    msn,
    expiryDate: null,
    asOfDate: null,
    deliveryDate: null,
    enableEolTrueup: false,
    eolModality: null,
    companionDefault: false,
    components: [],
    params: [],
    layers: [],
    overrides: {},
  };
}

/** Apply user edits; marks asOfDate override so CTA re-import won't clobber it. */
export function applyTrueupOverrides(inputs, patch = {}) {
  const next = { ...(inputs || emptyTrueupInputs()), ...(patch || {}) };
  next.overrides = { ...(inputs?.overrides || {}) };
  if (patch.asOfDate != null) next.overrides.asOfDate = true;
  if (patch.deliveryDate != null) next.overrides.deliveryDate = true;
  if (patch.enableEolTrueup != null) next.enableEolTrueup = Boolean(patch.enableEolTrueup);
  return next;
}
